let audioCtx: AudioContext | null = null
let audioUnlocked = false
let initRegistered = false

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null
  if (audioCtx && audioCtx.state !== 'closed') return audioCtx
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext
  if (!Ctor) return null
  audioCtx = new Ctor()
  return audioCtx
}

function unlockAudio(): void {
  const ctx = getAudioContext()
  if (ctx && ctx.state === 'suspended') {
    void ctx.resume()
  }
  audioUnlocked = true
}

function unlockHandler(): void {
  // Starting an AudioContext opens the audio device, which can block for
  // 100ms+; doing it inside the first click made that click visibly lag. It
  // runs just after the click paints, still within the gesture's activation.
  requestAnimationFrame(() => setTimeout(unlockAudio, 0))
  if (typeof window !== 'undefined' && 'Notification' in window) {
    if (Notification.permission === 'default') {
      void Notification.requestPermission()
    }
  }
  removeUnlockListeners()
}

function removeUnlockListeners(): void {
  if (typeof window === 'undefined') return
  window.removeEventListener('pointerdown', unlockHandler)
  window.removeEventListener('keydown', unlockHandler)
  window.removeEventListener('touchstart', unlockHandler)
}

/** Install one-time listeners so the first user gesture unlocks audio and prompts for notification permission. */
export function initAudio(): void {
  if (typeof window === 'undefined' || initRegistered) return
  initRegistered = true
  window.addEventListener('pointerdown', unlockHandler, { once: true })
  window.addEventListener('keydown', unlockHandler, { once: true })
  window.addEventListener('touchstart', unlockHandler, { once: true })
}

function playTone(
  frequency: number,
  durationMs: number,
  startDelayMs: number = 0,
  volume: number = 0.18,
): void {
  const ctx = getAudioContext()
  if (!ctx) return
  if (ctx.state === 'suspended') {
    void ctx.resume()
    if (!audioUnlocked) return
  }
  const startTime = ctx.currentTime + startDelayMs / 1000
  const endTime = startTime + durationMs / 1000
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  osc.type = 'sine'
  osc.frequency.setValueAtTime(frequency, startTime)
  gain.gain.setValueAtTime(0, startTime)
  gain.gain.linearRampToValueAtTime(volume, startTime + 0.01)
  gain.gain.exponentialRampToValueAtTime(0.0001, endTime)
  osc.connect(gain)
  gain.connect(ctx.destination)
  osc.start(startTime)
  osc.stop(endTime + 0.05)
}

/** Rising two-note chime for clock-in (C5 → E5). */
export function playClockInSound(): void {
  playTone(523.25, 140)
  playTone(659.25, 180, 130)
}

/** Falling two-note chime for clock-out (E5 → C5). */
export function playClockOutSound(): void {
  playTone(659.25, 140)
  playTone(523.25, 180, 130)
}

/** Two quick high beeps for notification alerts. */
export function playNotificationSound(): void {
  playTone(880, 90, 0, 0.15)
  playTone(880, 90, 140, 0.15)
}

export interface NotifyOptions {
  title: string
  body?: string
}

/** Plays the alert sound and, if permission was granted, shows a native browser notification. */
export function notifyUser(options: NotifyOptions): void {
  playNotificationSound()
  if (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    Notification.permission === 'granted'
  ) {
    try {
      new Notification(options.title, { body: options.body })
    } catch {
      // Safari/iOS may throw — sound still plays.
    }
  }
}
